import React, { useState, useEffect, useRef } from "react";
import {
  searchContributorByName,
  searchUsersByEmail,
} from "../../services/salesforceApi";

/**
 * UserLookupField Component
 *
 * A field for entering Salesforce User ID or email
 * Supports both simple text input and searchable dropdown modes
 *
 * @param {string} label - Field label
 * @param {string} value - Current value
 * @param {function} onChange - Callback when value changes: (value, userId) => void
 * @param {string} placeholder - Input placeholder
 * @param {boolean} required - Is field required
 * @param {boolean} disabled - Is field disabled
 * @param {boolean} searchable - Enable search dropdown functionality
 */
const UserLookupField = ({
  label,
  value = "",
  onChange,
  placeholder = "Enter Salesforce ID or email",
  required = false,
  disabled = false,
  showError = false,
  searchable = false,
}) => {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update searchQuery when value prop changes
  useEffect(() => {
    if (searchable && value !== searchQuery) {
      setSearchQuery(value || "");
    }
  }, [value, searchable, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!searchable) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchable]);

  // Handle search with debouncing
  const handleSearch = async (query) => {
    if (!searchable || query.length < 3) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    let searchFunction;
    if (label === "Project Manager") {
      searchFunction = searchContributorByName;
    } else searchFunction = searchUsersByEmail;

    setIsSearching(true);
    setShowDropdown(true);

    try {
      const results = await searchFunction(query);
      setSearchResults(results || []);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;

    if (searchable) {
      setSearchQuery(newValue);
      setSelectedUserId(null);

      // Debounce search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(newValue);
      }, 300);

      // Update parent with email
      onChange(newValue, null);
    } else {
      // Simple mode - just pass the value
      onChange(newValue, null);
    }
  };

  const handleSelectUser = (user) => {
    if (label === "Project Manager") {
      setSearchQuery(user.Name);
      setSelectedUserId(user.Id);
    } else {
      setSearchQuery(user.Email);
      setSelectedUserId(user.Id);
    }
    setShowDropdown(false);
    setSearchResults([]);

    // Pass both email and userId to parent
    if (label === "Project Manager") {
      onChange(user.Name, user.Id);
    } else onChange(user.Email, user.Id);
  };

  const handleChange = (e) => {
    handleInputChange(e);
  };

  // Determine border color
  const getBorderColor = () => {
    if (showError && required && !value && !searchQuery)
      return "border-red-500 bg-red-50";
    return "border-gray-300";
  };

  const displayValue = searchable ? searchQuery : value;

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          placeholder={
            searchable
              ? label === "Project Manager"
                ? "Enter name to search..."
                : "Type to search users by email..."
              : placeholder
          }
          disabled={disabled}
          className={`w-full px-3 py-2 text-sm border ${getBorderColor()} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            disabled
              ? "bg-gray-50 cursor-not-allowed text-gray-500"
              : "hover:border-gray-400"
          } ${searchable ? "pr-10" : ""}`}
        />
        {searchable && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Search Dropdown */}
      {searchable && showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {isSearching ? (
            <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
          ) : searchResults.length > 0 ? (
            <ul>
              {searchResults.map((user) => (
                <li
                  key={user.Id}
                  onClick={() => handleSelectUser(user)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <div className="text-sm font-medium text-gray-900">
                    {user.Name}
                  </div>
                  <div className="text-xs text-gray-500">{user.Email}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">
              {searchQuery.length < 3
                ? "Type at least 3 characters to search"
                : "No users found"}
            </div>
          )}
        </div>
      )}

      {/* Show error if required and empty */}
      {showError && required && !value && !searchQuery && (
        <p className="text-xs text-red-600 mt-1">This field is required</p>
      )}
    </div>
  );
};

export default UserLookupField;
